import { useMemo } from "react";

const petalColors = [
  "hsl(350 58% 62%)",    // Vibrant Rose
  "hsl(350 52% 72%)",    // Light rose
  "hsl(350 55% 52%)",    // Deeper blush
  "hsl(150 13% 61%)",    // Sage green
  "hsl(350 45% 78%)",    // Soft pink
  "hsl(40 45% 57%)",     // Muted gold
  "hsl(330 50% 68%)",    // Mauve pink
];

// Detailed flower bud & bloom SVGs
const flowerShapes = [
  // Rose bud — layered petals
  (color: string) => (
    <svg viewBox="0 0 32 38" fill="none" className="w-full h-full">
      <ellipse cx="16" cy="28" rx="4" ry="2" fill="hsl(150 20% 45%)" fillOpacity="0.5" />
      <path d="M16 38V24" stroke="hsl(150 20% 40%)" strokeWidth="1.2" strokeOpacity="0.6" />
      <path d="M12 26C10 24 9 20 12 16C12 16 14 20 16 20C18 20 20 16 20 16C23 20 22 24 20 26C18 28 14 28 12 26Z" fill={color} fillOpacity="0.75" />
      <path d="M14 24C13 22 12.5 19 14.5 16C14.5 16 15.5 19 16 19C16.5 19 17.5 16 17.5 16C19.5 19 19 22 18 24C17 25.5 15 25.5 14 24Z" fill={color} fillOpacity="0.9" />
      <path d="M15 22C14.5 21 14.5 18.5 16 17C17.5 18.5 17.5 21 17 22C16.5 22.8 15.5 22.8 15 22Z" fill={color} fillOpacity="1" />
      <path d="M11 22L9 18C9 18 11 17 13 18" stroke="hsl(150 18% 50%)" strokeWidth="0.6" strokeOpacity="0.4" fill="none" />
      <path d="M21 22L23 18C23 18 21 17 19 18" stroke="hsl(150 18% 50%)" strokeWidth="0.6" strokeOpacity="0.4" fill="none" />
      <path d="M10 20C8 21 7 19 8 17" stroke="hsl(150 25% 45%)" strokeWidth="0.7" fill="hsl(150 20% 55%)" fillOpacity="0.35" />
      <path d="M22 20C24 21 25 19 24 17" stroke="hsl(150 25% 45%)" strokeWidth="0.7" fill="hsl(150 20% 55%)" fillOpacity="0.35" />
    </svg>
  ),
  // Peony bloom — full fluffy flower
  (color: string) => (
    <svg viewBox="0 0 36 36" fill="none" className="w-full h-full">
      <circle cx="18" cy="8" r="6" fill={color} fillOpacity="0.4" />
      <circle cx="8" cy="16" r="6" fill={color} fillOpacity="0.4" />
      <circle cx="28" cy="16" r="6" fill={color} fillOpacity="0.4" />
      <circle cx="10" cy="26" r="6" fill={color} fillOpacity="0.4" />
      <circle cx="26" cy="26" r="6" fill={color} fillOpacity="0.4" />
      <circle cx="18" cy="14" r="5" fill={color} fillOpacity="0.6" />
      <circle cx="13" cy="20" r="5" fill={color} fillOpacity="0.55" />
      <circle cx="23" cy="20" r="5" fill={color} fillOpacity="0.55" />
      <circle cx="18" cy="22" r="4.5" fill={color} fillOpacity="0.7" />
      <circle cx="18" cy="18" r="3" fill="hsl(40 50% 65%)" fillOpacity="0.7" />
      <circle cx="17" cy="17" r="1" fill="hsl(40 55% 55%)" fillOpacity="0.5" />
      <circle cx="19" cy="19" r="1" fill="hsl(40 55% 55%)" fillOpacity="0.5" />
    </svg>
  ),
  // Cherry blossom — 5 petals with notch
  (color: string) => (
    <svg viewBox="0 0 30 30" fill="none" className="w-full h-full">
      <g transform="translate(15,15)">
        {[0, 72, 144, 216, 288].map((angle) => (
          <g key={angle} transform={`rotate(${angle})`}>
            <path d="M0,-12C-3,-8 -3,-4 0,-1C3,-4 3,-8 0,-12Z" fill={color} fillOpacity="0.65" />
            <path d="M0,-11C-1,-8 -1,-5 0,-3C1,-5 1,-8 0,-11Z" fill={color} fillOpacity="0.85" />
          </g>
        ))}
      </g>
      <circle cx="15" cy="15" r="2.5" fill="hsl(40 50% 60%)" fillOpacity="0.8" />
      <circle cx="14" cy="14" r="0.7" fill="hsl(40 60% 50%)" fillOpacity="0.6" />
      <circle cx="16" cy="14.5" r="0.7" fill="hsl(40 60% 50%)" fillOpacity="0.6" />
      <circle cx="15" cy="16" r="0.7" fill="hsl(40 60% 50%)" fillOpacity="0.6" />
    </svg>
  ),
  // Dahlia — multi-layered pointed petals
  (color: string) => (
    <svg viewBox="0 0 34 34" fill="none" className="w-full h-full">
      <g transform="translate(17,17)">
        {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
          <g key={angle} transform={`rotate(${angle})`}>
            <path d="M0,-14C-2,-10 -2,-6 0,-2C2,-6 2,-10 0,-14Z" fill={color} fillOpacity="0.45" />
          </g>
        ))}
        {[22, 67, 112, 157, 202, 247, 292, 337].map((angle) => (
          <g key={angle} transform={`rotate(${angle})`}>
            <path d="M0,-10C-1.5,-7 -1.5,-4 0,-1C1.5,-4 1.5,-7 0,-10Z" fill={color} fillOpacity="0.65" />
          </g>
        ))}
        {[0, 60, 120, 180, 240, 300].map((angle) => (
          <g key={angle} transform={`rotate(${angle})`}>
            <path d="M0,-6C-1,-4 -1,-2 0,0C1,-2 1,-4 0,-6Z" fill={color} fillOpacity="0.85" />
          </g>
        ))}
      </g>
      <circle cx="17" cy="17" r="2" fill="hsl(40 50% 60%)" fillOpacity="0.8" />
    </svg>
  ),
  // Tulip bud — closed elegant shape
  (color: string) => (
    <svg viewBox="0 0 24 38" fill="none" className="w-full h-full">
      <path d="M12 38V22" stroke="hsl(150 20% 40%)" strokeWidth="1.5" strokeOpacity="0.6" />
      <path d="M8 28C6 26 5 22 7 17C9 12 12 10 12 10C12 10 15 12 17 17C19 22 18 26 16 28C14 30 10 30 8 28Z" fill={color} fillOpacity="0.7" />
      <path d="M10 26C9 24 8.5 21 10 17C11 14 12 12 12 12C12 12 13 14 14 17C15.5 21 15 24 14 26C13 27.5 11 27.5 10 26Z" fill={color} fillOpacity="0.85" />
      <path d="M11.5 24C11 22.5 11 20 12 17C13 20 13 22.5 12.5 24C12.2 24.8 11.8 24.8 11.5 24Z" fill={color} fillOpacity="1" />
      <path d="M7 24L5 20C6 18.5 8 18 8 18" stroke="hsl(150 20% 50%)" strokeWidth="0.5" fill="hsl(150 20% 50%)" fillOpacity="0.25" />
      <path d="M17 24L19 20C18 18.5 16 18 16 18" stroke="hsl(150 20% 50%)" strokeWidth="0.5" fill="hsl(150 20% 50%)" fillOpacity="0.25" />
    </svg>
  ),
  // Lily — elegant open flower
  (color: string) => (
    <svg viewBox="0 0 36 34" fill="none" className="w-full h-full">
      <g transform="translate(18,20)">
        {[0, 60, 120, 180, 240, 300].map((angle) => (
          <g key={angle} transform={`rotate(${angle})`}>
            <path d="M0,-14C-3,-10 -4,-5 -1,-1C0,0 0,0 1,-1C4,-5 3,-10 0,-14Z" fill={color} fillOpacity="0.55" />
            <path d="M0,-12C-1.5,-8 -1.5,-4 0,-1C1.5,-4 1.5,-8 0,-12Z" fill={color} fillOpacity="0.75" />
          </g>
        ))}
      </g>
      <circle cx="18" cy="20" r="3" fill="hsl(40 50% 65%)" fillOpacity="0.7" />
      {[0, 60, 120, 180, 240, 300].map((angle) => (
        <line
          key={angle}
          x1="18"
          y1="20"
          x2={18 + Math.cos((angle * Math.PI) / 180) * 5}
          y2={20 + Math.sin((angle * Math.PI) / 180) * 5}
          stroke="hsl(40 55% 55%)"
          strokeWidth="0.6"
          strokeOpacity="0.5"
        />
      ))}
    </svg>
  ),
];

interface Petal {
  id: number;
  x: number;
  size: number;
  duration: number;
  delay: number;
  swayAmount: number;
  shape: number;
  color: string;
  startRotation: number;
}

export function FloatingPetals() {
  const petals = useMemo<Petal[]>(() => {
    return Array.from({ length: 14 }, (_, i) => ({
      id: i,
      x: 5 + Math.random() * 90,
      size: 18 + Math.random() * 20,
      duration: 10 + Math.random() * 8,
      delay: Math.random() * 14,
      swayAmount: 15 + Math.random() * 30,
      shape: Math.floor(Math.random() * flowerShapes.length),
      color: petalColors[Math.floor(Math.random() * petalColors.length)],
      startRotation: Math.random() * 360,
    }));
  }, []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[2]" aria-hidden="true">
      {petals.map((p) => (
        <div
          key={p.id}
          className="absolute petal-float"
          style={{
            left: `${p.x}%`,
            bottom: "-50px",
            width: `${p.size}px`,
            height: `${p.size * 1.2}px`,
            animationDuration: `${p.duration}s`,
            animationDelay: `${p.delay}s`,
            "--sway": `${p.swayAmount}px`,
            "--start-rotation": `${p.startRotation}deg`,
          } as React.CSSProperties}
        >
          {flowerShapes[p.shape](p.color)}
        </div>
      ))}
    </div>
  );
}
